import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Attendee, Meeting } from '@aws-sdk/client-chime-sdk-meetings';
import { Observable } from 'rxjs';
import { EnvService } from 'src/app/env.service';

export interface ChatMessage {
  timestamp: number;
  senderName: string;
  senderId: string;
  message: string;
}

// Define strongly-typed interfaces for the backend responses
export interface MeetingResponse {
  Meeting: Meeting;
  startedAt?: string;
  chatMessages?: ChatMessage[];
  isAudioLocked?: boolean;
  isCameraLocked?: boolean;
  isScreenShareLocked?: boolean;
  isChatLocked?: boolean;
  isWaitingRoomEnabled: boolean;
  geminiInteraction: string;
}

export interface AttendeeResponse {
  Attendee: Attendee;
}

export interface InviteResponse {
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChimeCallService {
  private http = inject(HttpClient);
  private env = inject(EnvService);

  private apiUrl = `${this.env.apiUrl}chime`;
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  /**
   * Calls the backend to create a new AWS Chime meeting session.
   * @param callID - The unique identifier for the call in your application's database.
   */
  createChimeMeeting(body: {
    callID: string;
    isAudioLocked?: boolean;
    isCameraLocked?: boolean;
    isScreenShareLocked?: boolean;
    isChatLocked?: boolean;
    isWaitingRoomEnabled?: boolean;
    courseId?: string;
    noteId?: string;
    blogId?: string;
  }): Observable<MeetingResponse> {
    return this.http.post<MeetingResponse>(`${this.apiUrl}/create-meeting`, body, this.httpOptions);
  }

  /**
   * Calls the backend to create an attendee for the specified Chime meeting.
   * @param meetingId - The MeetingId provided by AWS Chime.
   * @param userId - The current user's unique ID from your application.
   * @param username - The display name for the current user.
   */
  joinChimeMeeting(meetingId: string, userId: string, username: string): Observable<AttendeeResponse> {
    const body = { meetingId, userId, username };
    return this.http.post<AttendeeResponse>(`${this.apiUrl}/join-meeting`, body, this.httpOptions);
  }

  /**
   * Calls the backend to send an email invitation to a participant.
   * @param callID - The ID of the call to invite to.
   * @param destinationEmail - The email address of the invitee.
   * @param name - The optional name of the invitee.
   */
  inviteToCall(callID: string, destinationEmail: string, name?: string): Observable<InviteResponse> {
    const body = { callID, destinationEmail, name };
    return this.http.post<InviteResponse>(`${this.apiUrl}/invite`, body, this.httpOptions);
  }

  inviteToCallBulk(callID: string, emails: string[] | { email: string; name?: string }[]): Observable<any> {
    const body = { callID, emails };
    return this.http.post<any>(`${this.apiUrl}/invite`, body, this.httpOptions);
  }

  startRecording(meetingId: string, callId: string): Observable<any> {
    const body = { meetingId, callID: callId };
    return this.http.post<any>(`${this.apiUrl}/start-recording`, body, this.httpOptions);
  }

  stopRecording(callId: string): Observable<any> {
    const body = { callID: callId };
    return this.http.post<any>(`${this.apiUrl}/stop-recording`, body, this.httpOptions);
  }

  startPstnCall(meetingId: string, attendeeId: string, toPhoneNumber: string, callID: string): Observable<{ message: string; transactionId: string }> {
    const body = { meetingId, attendeeId, toPhoneNumber, callID };
    return this.http.post<{ message: string; transactionId: string }>(`${this.apiUrl}/start-pstn-call`, body, this.httpOptions);
  }

  endCallAndMeeting(callID: string): Observable<any> {
    const body = { callID };
    return this.http.post<any>(`${this.apiUrl}/end-call`, body, this.httpOptions);
  }

  previewPstnGreeting(text: string, languageCode: string): Observable<Blob> {
    const body = { text, languageCode };
    return this.http.post(`${this.apiUrl}/greeting/preview`, body, { responseType: 'blob' });
  }

  uploadPstnGreeting(text: string, languageCode: string, audioData: string, mimeType: string): Observable<{ message: string; key: string }> {
    const body = { text, languageCode, audioData, mimeType };
    return this.http.post<{ message: string; key: string }>(`${this.apiUrl}/greeting/upload`, body, this.httpOptions);
  }

  saveChatMessage(callID: string, message: ChatMessage): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/messages`, message, this.httpOptions);
  }

  getCallSettings(
    callID: string
  ): Observable<{ isAudioLocked: boolean; isCameraLocked: boolean; isScreenShareLocked: boolean; isChatLocked: boolean; isWaitingRoomEnabled: boolean; createdBy?: string }> {
    return this.http.get<{
      isAudioLocked: boolean;
      isCameraLocked: boolean;
      isScreenShareLocked: boolean;
      isChatLocked: boolean;
      isWaitingRoomEnabled: boolean;
      createdBy?: string;
    }>(`${this.apiUrl}/calls/${callID}/settings`, this.httpOptions);
  }

  saveCallSettings(
    callID: string,
    settings: { isAudioLocked?: boolean; isCameraLocked?: boolean; isScreenShareLocked?: boolean; isChatLocked?: boolean; isWaitingRoomEnabled?: boolean }
  ): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/settings`, settings, this.httpOptions);
  }

  verifyGuest(callID: string, email: string, name: string, uid: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/verify-guest`, { email, name, uid }, this.httpOptions);
  }

  admitParticipant(callID: string, guestUserId: string, guestUsername: string, guestEmail?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/admit`, { guestUserId, guestUsername, guestEmail }, this.httpOptions);
  }

  rejectParticipant(callID: string, guestUserId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/reject`, { guestUserId }, this.httpOptions);
  }

  kickParticipant(callID: string, attendeeId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/kick`, { attendeeId }, this.httpOptions);
  }

  getInvitations(callID: string): Observable<{ invitations: { email: string; name: string; invitedAt: Date; joined: boolean; joinedAt?: Date }[] }> {
    return this.http.get<{ invitations: any[] }>(`${this.apiUrl}/calls/${callID}/invitations`, this.httpOptions);
  }

  addInvitation(callID: string, entityId: string, type: 'user' | 'group' = 'user', name?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/${callID}/invitations`, { entityId, type, name }, this.httpOptions);
  }

  removeInvitation(callID: string, entityId: string, type: 'user' | 'group' = 'user'): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/calls/${callID}/invitations`, { ...this.httpOptions, params: { entityId, type } });
  }

  getGroup(groupId: string): Observable<any> {
    return this.http.get<any>(`${this.env.apiUrl}groups/get`, {
      headers: this.httpOptions.headers,
      params: { id: groupId },
    });
  }
}
